import { useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Truck,
  RefreshCw,
  MapPin,
  X,
  Maximize2,
  Minimize2,
  Navigation,
  AlertCircle,
  AlertTriangle,
  User,
  Package,
  Search,
  Calendar,
  RotateCcw,
  ChevronDown,
  Check,
} from 'lucide-react';
import { deliveriesApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import type { DriverLocation, DeliveryMapItem } from '@/types';

interface DeliveryLiveMapProps {
  onClose?: () => void;
  isModal?: boolean;
  initialStatus?: 'All' | 'Active' | 'Out for Delivery' | 'Pending' | 'Delivered';
  initialDriver?: string;
  initialFromDate?: string;
  initialToDate?: string;
}

function escapeHtml(str: string): string {
  return (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getYesterdayDateString(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getSevenDaysAgoDateString(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function DeliveryLiveMap({
  onClose,
  isModal = false,
  initialStatus = 'All',
  initialDriver = '',
  initialFromDate,
  initialToDate,
}: DeliveryLiveMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const routesLayerRef = useRef<L.LayerGroup | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);

  // Filter & UI State
  const [activeSideTab, setActiveSideTab] = useState<'deliveries' | 'riders'>('deliveries');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<'All' | 'Active' | 'Out for Delivery' | 'Pending' | 'Delivered'>(
    initialStatus
  );
  const [selectedDriverFilter, setSelectedDriverFilter] = useState<string>(initialDriver);
  const [locationFilter, setLocationFilter] = useState<'all' | 'plotted' | 'missing'>('all');
  const [datePreset, setDatePreset] = useState<'today' | 'yesterday' | 'week' | 'all' | 'custom'>(
    initialFromDate && initialFromDate !== getTodayDateString() ? 'custom' : 'today'
  );
  const [fromDate, setFromDate] = useState<string>(initialFromDate || getTodayDateString());
  const [toDate, setToDate] = useState<string>(initialToDate || getTodayDateString());
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown menus state
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
        setDriverDropdownOpen(false);
        setDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [showRidersLayer, setShowRidersLayer] = useState(true);
  const [showDeliveriesLayer, setShowDeliveriesLayer] = useState(true);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Fetch Drivers and Deliveries (refetches on date change)
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['deliveryDriversLocations', datePreset === 'all' ? '' : fromDate, datePreset === 'all' ? '' : toDate],
    queryFn: async () => {
      const params =
        datePreset === 'all'
          ? undefined
          : {
              from_date: fromDate,
              to_date: toDate,
            };
      const res = await deliveriesApi.getDriversLocations(params);
      return (res.data.message || { drivers: [], deliveries: [], out_for_delivery_orders: [], store: {} }) as {
        drivers: DriverLocation[];
        deliveries: DeliveryMapItem[];
        out_for_delivery_orders: any[];
        store: any;
      };
    },
    refetchInterval: autoRefresh ? 20_000 : false,
    staleTime: 10_000,
  });

  const drivers = data?.drivers || [];
  const rawDeliveries = data?.deliveries || [];

  // Handle Date Presets
  const handleDatePresetChange = (preset: 'today' | 'yesterday' | 'week' | 'all' | 'custom') => {
    setDatePreset(preset);
    const today = getTodayDateString();
    if (preset === 'today') {
      setFromDate(today);
      setToDate(today);
    } else if (preset === 'yesterday') {
      const yest = getYesterdayDateString();
      setFromDate(yest);
      setToDate(yest);
    } else if (preset === 'week') {
      setFromDate(getSevenDaysAgoDateString());
      setToDate(today);
    } else if (preset === 'all') {
      setFromDate('');
      setToDate('');
    }
  };

  // Comprehensive Counts
  const counts = useMemo(() => {
    const totalOut = rawDeliveries.filter((d) => d.delivery_status === 'Out for Delivery').length;
    const totalPending = rawDeliveries.filter((d) => d.delivery_status === 'Pending').length;
    const totalDelivered = rawDeliveries.filter((d) => d.delivery_status === 'Delivered').length;
    const totalActive = totalOut + totalPending;
    const unassignedCount = rawDeliveries.filter((d) => !d.delivery_driver || d.delivery_driver.trim() === '').length;
    const missingCoordsCount = rawDeliveries.filter((d) => d.latitude === 0 && d.longitude === 0).length;
    const plottedCount = rawDeliveries.filter((d) => d.latitude !== 0 || d.longitude !== 0).length;
    const onlineRiders = drivers.filter((d) => d.is_online).length;

    return {
      all: rawDeliveries.length,
      active: totalActive,
      out: totalOut,
      pending: totalPending,
      delivered: totalDelivered,
      unassigned: unassignedCount,
      missingCoords: missingCoordsCount,
      plotted: plottedCount,
      onlineRiders,
      totalRiders: drivers.length,
    };
  }, [rawDeliveries, drivers]);

  // Filter deliveries according to status, driver, location, and search
  const deliveries = useMemo(() => {
    return rawDeliveries.filter((d) => {
      // 1. Status Filter
      if (deliveryStatusFilter === 'Active') {
        if (d.delivery_status !== 'Pending' && d.delivery_status !== 'Out for Delivery') {
          return false;
        }
      } else if (deliveryStatusFilter !== 'All' && d.delivery_status !== deliveryStatusFilter) {
        return false;
      }

      // 2. Driver Filter
      if (selectedDriverFilter === '__unassigned__') {
        if (d.delivery_driver && d.delivery_driver.trim() !== '') {
          return false;
        }
      } else if (selectedDriverFilter) {
        const normFilter = selectedDriverFilter.trim().toLowerCase();
        const normDriver = (d.delivery_driver || '').trim().toLowerCase();
        if (normDriver !== normFilter && !normDriver.includes(normFilter)) {
          return false;
        }
      }

      // 3. Location Filter (plotted vs missing coords)
      const hasCoords = d.latitude !== 0 && d.longitude !== 0;
      if (locationFilter === 'plotted' && !hasCoords) {
        return false;
      }
      if (locationFilter === 'missing' && hasCoords) {
        return false;
      }

      // 4. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = (d.invoice_name || '').toLowerCase().includes(q);
        const matchCust = (d.customer_name || '').toLowerCase().includes(q);
        const matchRecipient = (d.delivery_recipient_name || '').toLowerCase().includes(q);
        const matchDriver = (d.delivery_driver || '').toLowerCase().includes(q);
        const matchAddr = (d.delivery_address || '').toLowerCase().includes(q);
        const matchPc = (d.delivery_postal_code || '').toLowerCase().includes(q);
        const matchPhone = (d.delivery_phone || d.customer_mobile || '').toLowerCase().includes(q);
        if (!matchName && !matchCust && !matchRecipient && !matchDriver && !matchAddr && !matchPc && !matchPhone) {
          return false;
        }
      }

      return true;
    });
  }, [rawDeliveries, deliveryStatusFilter, selectedDriverFilter, locationFilter, searchQuery]);

  // Filter drivers according to selected driver
  const filteredDrivers = useMemo(() => {
    if (!selectedDriverFilter || selectedDriverFilter === '__unassigned__') {
      return drivers;
    }
    const norm = selectedDriverFilter.trim().toLowerCase();
    return drivers.filter((dr) => {
      const matchName = (dr.full_name || '').toLowerCase().includes(norm);
      const matchUser = (dr.user || '').toLowerCase().includes(norm);
      const matchId = (dr.id || '').toLowerCase().includes(norm);
      return matchName || matchUser || matchId;
    });
  }, [drivers, selectedDriverFilter]);

  // Drivers with coordinates
  const driversWithLocation = useMemo(
    () => filteredDrivers.filter((d) => d.has_location && d.latitude !== 0 && d.longitude !== 0),
    [filteredDrivers],
  );

  // Deliveries with coordinates
  const deliveriesWithLocation = useMemo(
    () => deliveries.filter((d) => d.latitude !== 0 && d.longitude !== 0),
    [deliveries],
  );

  // Active filters count for reset badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (deliveryStatusFilter !== 'All') count++;
    if (selectedDriverFilter !== '') count++;
    if (locationFilter !== 'all') count++;
    if (searchQuery.trim() !== '') count++;
    if (datePreset !== 'today') count++;
    return count;
  }, [deliveryStatusFilter, selectedDriverFilter, locationFilter, searchQuery, datePreset]);

  // Reset all filters
  const resetAllFilters = () => {
    setDeliveryStatusFilter('All');
    setSelectedDriverFilter('');
    setLocationFilter('all');
    setSearchQuery('');
    handleDatePresetChange('today');
  };

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapInstanceRef.current) return;

    const defaultCenter: [number, number] = [51.5074, -0.1278]; // Default UK / London coordinate fallback

    const map = L.map(mapContainerRef.current, {
      center: defaultCenter,
      zoom: 12,
      zoomControl: false,
    });

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const routesGroup = L.layerGroup().addTo(map);
    const markersGroup = L.layerGroup().addTo(map);
    routesLayerRef.current = routesGroup;
    markersLayerRef.current = markersGroup;
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
      routesLayerRef.current = null;
    };
  }, []);

  // Update Markers and Connections
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    const routesLayer = routesLayerRef.current;
    if (!map || !layer || !routesLayer) return;

    layer.clearLayers();
    routesLayer.clearLayers();

    const bounds = L.latLngBounds([]);

    // 1. PLOT RIDERS
    if (showRidersLayer) {
      driversWithLocation.forEach((driver) => {
        const isOnline = driver.is_online;
        const ringColor = isOnline ? '#10b981' : '#f59e0b';
        const safeName = escapeHtml(driver.full_name);
        const ordersCount = driver.active_orders_count || 0;

        const customRiderIcon = L.divIcon({
          className: 'custom-rider-marker',
          html: `
            <div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer;">
              ${
                isOnline
                  ? `<span style="position: absolute; top: 0; width: 44px; height: 44px; border-radius: 9999px; background-color: ${ringColor}; opacity: 0.35; animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;"></span>`
                  : ''
              }
              <div style="position: relative; width: 42px; height: 42px; border-radius: 9999px; background: #ffffff; border: 3px solid ${ringColor}; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3); display: flex; align-items: center; justify-content: center;">
                <svg style="width: 22px; height: 22px; color: ${ringColor}; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round;" viewBox="0 0 24 24">
                  <rect x="1" y="3" width="15" height="13"></rect>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
                ${
                  ordersCount > 0
                    ? `<span style="position: absolute; top: -5px; right: -5px; background: #4f46e5; color: #ffffff; font-size: 11px; font-weight: 700; width: 19px; height: 19px; border-radius: 9999px; display: flex; align-items: center; justify-content: center; border: 2px solid #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">${ordersCount}</span>`
                    : ''
                }
              </div>
              <div style="margin-top: 3px; background: rgba(255, 255, 255, 0.96); backdrop-filter: blur(4px); color: #0f172a; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 9999px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18); border: 1px solid #cbd5e1; white-space: nowrap;">
                🚴 ${safeName}
              </div>
            </div>
          `,
          iconSize: [44, 68],
          iconAnchor: [22, 38],
          popupAnchor: [0, -38],
        });

        const marker = L.marker([driver.latitude, driver.longitude], { icon: customRiderIcon });

        const lastSeenText = driver.last_location_update
          ? driver.minutes_ago !== null && driver.minutes_ago !== undefined
            ? driver.minutes_ago === 0
              ? 'Just now'
              : `${driver.minutes_ago} min ago`
            : new Date(driver.last_location_update).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'Unknown';

        const ordersListHtml =
          driver.orders && driver.orders.length > 0
            ? driver.orders
                .map(
                  (o) => `
                  <div style="border-top: 1px solid #e2e8f0; padding-top: 6px; margin-top: 6px;">
                    <div style="display: flex; justify-content: space-between; font-weight: 600; color: #1e293b; font-size: 12px;">
                      <span>${escapeHtml(o.invoice_name)}</span>
                      <span style="color: ${o.delivery_status === 'Delivered' ? '#10b981' : o.delivery_status === 'Out for Delivery' ? '#6366f1' : '#f59e0b'}; font-size: 10px; font-weight: 700;">
                        ${escapeHtml(o.delivery_status || 'Out for Delivery')}
                      </span>
                    </div>
                    <div style="font-size: 11px; color: #475569; margin-top: 2px;">
                      <strong>Customer:</strong> ${escapeHtml(o.customer_name)}
                    </div>
                    ${
                      o.phone
                        ? `<div style="font-size: 11px; color: #475569;"><strong>Phone:</strong> <a href="tel:${escapeHtml(o.phone)}" style="color: #2563eb; text-decoration: underline;">${escapeHtml(o.phone)}</a></div>`
                        : ''
                    }
                    ${
                      o.address
                        ? `<div style="font-size: 11px; color: #64748b; margin-top: 2px; line-height: 1.3;">${escapeHtml(o.address)}</div>`
                        : ''
                    }
                  </div>
                `,
                )
                .join('')
            : '<div style="font-size: 11px; color: #94a3b8; font-style: italic; margin-top: 4px;">No assigned deliveries</div>';

        const popupHtml = `
          <div style="font-family: inherit; min-width: 230px; max-width: 290px; padding: 2px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
              <strong style="font-size: 14px; color: #0f172a;">${safeName}</strong>
              <span style="display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 2px 6px; border-radius: 9999px; ${
                isOnline ? 'background: #dcfce7; color: #15803d;' : 'background: #fef3c7; color: #b45309;'
              }">
                <span style="width: 6px; height: 6px; border-radius: 9999px; background: currentColor;"></span>
                ${isOnline ? 'Online' : 'Idle'}
              </span>
            </div>
            <div style="font-size: 11px; color: #64748b; margin-bottom: 8px;">
              Live GPS update: <strong>${lastSeenText}</strong>
            </div>
            <div style="font-size: 11px; font-weight: 700; color: #334155; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">
              Carrying Deliveries (${ordersCount})
            </div>
            <div style="max-height: 160px; overflow-y: auto;">
              ${ordersListHtml}
            </div>
          </div>
        `;

        marker.bindPopup(popupHtml, { className: 'driver-location-popup' });
        marker.on('click', () => {
          setSelectedEntityId(driver.id);
          setActiveSideTab('riders');
          drawRoutesForDriver(driver);
        });

        marker.addTo(layer);
        bounds.extend([driver.latitude, driver.longitude]);
      });
    }

    // 2. PLOT DELIVERIES
    if (showDeliveriesLayer) {
      deliveriesWithLocation.forEach((delivery) => {
        const status = delivery.delivery_status || 'Pending';
        let pinColor = '#f59e0b'; // Amber for Pending
        let statusBadgeBg = '#fef3c7';
        let statusBadgeText = '#b45309';

        if (status === 'Out for Delivery') {
          pinColor = '#6366f1'; // Indigo for In-Transit
          statusBadgeBg = '#e0e7ff';
          statusBadgeText = '#4338ca';
        } else if (status === 'Delivered') {
          pinColor = '#10b981'; // Green for Delivered
          statusBadgeBg = '#dcfce7';
          statusBadgeText = '#15803d';
        }

        const safeInvoice = escapeHtml(delivery.invoice_name);
        const safeCustomer = escapeHtml(delivery.customer_name || delivery.delivery_recipient_name || 'Customer');
        const safeDriver = escapeHtml(delivery.delivery_driver || 'Unassigned');

        const customDeliveryIcon = L.divIcon({
          className: 'custom-delivery-pin',
          html: `
            <div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer;">
              <!-- Pin Shape -->
              <div style="position: relative; width: 34px; height: 34px; border-radius: 9999px 9999px 2px 9999px; transform: rotate(45deg); background: ${pinColor}; border: 2.5px solid #ffffff; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3); display: flex; align-items: center; justify-content: center;">
                <svg style="width: 17px; height: 17px; color: #ffffff; transform: rotate(-45deg); fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round;" viewBox="0 0 24 24">
                  <path d="M16.5 9.4 7.55 4.24a1.78 1.78 0 0 0-2.5 1.55v12.42a1.78 1.78 0 0 0 2.5 1.55L16.5 14.6a1.78 1.78 0 0 0 0-3.2z"></path>
                </svg>
              </div>
              <!-- Status Label Badge -->
              <div style="margin-top: 5px; background: rgba(255, 255, 255, 0.96); backdrop-filter: blur(4px); color: #0f172a; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 9999px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18); white-space: nowrap; border: 1px solid #cbd5e1; border-left: 3.5px solid ${pinColor};">
                ${safeInvoice} • ${status}
              </div>
            </div>
          `,
          iconSize: [36, 56],
          iconAnchor: [18, 34],
          popupAnchor: [0, -34],
        });

        const deliveryMarker = L.marker([delivery.latitude, delivery.longitude], { icon: customDeliveryIcon });

        const phone = delivery.delivery_phone || delivery.customer_mobile;
        const deliveryPopupHtml = `
          <div style="font-family: inherit; min-width: 230px; max-width: 290px; padding: 2px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
              <strong style="font-size: 13px; color: #0f172a;">${safeInvoice}</strong>
              <span style="font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 9999px; background: ${statusBadgeBg}; color: ${statusBadgeText};">
                ${status}
              </span>
            </div>
            <div style="font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 2px;">
              Customer: <span style="font-weight: 700; color: #0f172a;">${safeCustomer}</span>
            </div>
            ${
              delivery.delivery_driver
                ? `<div style="font-size: 11px; color: #4338ca; background: #e0e7ff; padding: 3px 6px; border-radius: 6px; margin-bottom: 6px; font-weight: 600;">
                     Rider: 🚴 ${safeDriver}
                   </div>`
                : `<div style="font-size: 11px; color: #b45309; background: #fef3c7; padding: 3px 6px; border-radius: 6px; margin-bottom: 6px; font-weight: 600;">
                     Awaiting Rider Assignment
                   </div>`
            }
            ${
              phone
                ? `<div style="font-size: 11px; color: #475569; margin-bottom: 4px;"><strong>Phone:</strong> <a href="tel:${escapeHtml(phone)}" style="color: #2563eb; text-decoration: underline;">${escapeHtml(phone)}</a></div>`
                : ''
            }
            ${
              delivery.delivery_address
                ? `<div style="font-size: 11px; color: #64748b; line-height: 1.35; margin-bottom: 4px;">
                     <strong>Address:</strong> ${escapeHtml(delivery.delivery_address)} ${delivery.delivery_postal_code ? `(${escapeHtml(delivery.delivery_postal_code)})` : ''}
                   </div>`
                : ''
            }
            ${
              delivery.net_total
                ? `<div style="font-size: 12px; font-weight: 700; color: #0f172a; margin-top: 6px; border-top: 1px solid #e2e8f0; padding-top: 4px;">
                     Order Total: ${formatMoney(delivery.net_total)}
                   </div>`
                : ''
            }
          </div>
        `;

        deliveryMarker.bindPopup(deliveryPopupHtml, { className: 'delivery-location-popup' });
        deliveryMarker.on('click', () => {
          setSelectedEntityId(delivery.id);
          setActiveSideTab('deliveries');
        });

        deliveryMarker.addTo(layer);
        bounds.extend([delivery.latitude, delivery.longitude]);
      });
    }

    // Auto-fit if coordinates exist
    if ((driversWithLocation.length > 0 || deliveriesWithLocation.length > 0) && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [driversWithLocation, deliveriesWithLocation, showRidersLayer, showDeliveriesLayer]);

  // Draw dashed connection lines from a rider to their active deliveries
  const drawRoutesForDriver = (driver: DriverLocation) => {
    const map = mapInstanceRef.current;
    const routesLayer = routesLayerRef.current;
    if (!map || !routesLayer || !driver.has_location) return;

    routesLayer.clearLayers();

    const assignedDeliveries = deliveriesWithLocation.filter((d) => {
      const driverName = (d.delivery_driver || '').trim().toLowerCase();
      const driverFull = (driver.full_name || '').trim().toLowerCase();
      const driverUser = (driver.user || '').trim().toLowerCase();
      return driverName && (driverName === driverFull || driverName === driverUser);
    });

    assignedDeliveries.forEach((del) => {
      const polyline = L.polyline(
        [
          [driver.latitude, driver.longitude],
          [del.latitude, del.longitude],
        ],
        {
          color: '#6366f1',
          weight: 3,
          dashArray: '6, 8',
          opacity: 0.85,
        },
      );
      polyline.addTo(routesLayer);
    });
  };

  const fitAllMarkers = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const allPoints: [number, number][] = [];
    if (showRidersLayer) {
      driversWithLocation.forEach((d) => allPoints.push([d.latitude, d.longitude]));
    }
    if (showDeliveriesLayer) {
      deliveriesWithLocation.forEach((del) => allPoints.push([del.latitude, del.longitude]));
    }

    if (allPoints.length === 0) return;

    if (allPoints.length === 1) {
      map.flyTo(allPoints[0], 15);
      return;
    }

    const bounds = L.latLngBounds(allPoints);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
    }
  };

  const focusDriver = (driver: DriverLocation) => {
    setSelectedEntityId(driver.id);
    setActiveSideTab('riders');
    const map = mapInstanceRef.current;
    if (!map) return;

    if (driver.has_location && driver.latitude !== 0) {
      map.flyTo([driver.latitude, driver.longitude], 16, { duration: 1.2 });
      drawRoutesForDriver(driver);
    }
  };

  const focusDelivery = (delivery: DeliveryMapItem) => {
    setSelectedEntityId(delivery.id);
    setActiveSideTab('deliveries');
    const map = mapInstanceRef.current;
    if (!map) return;

    if (delivery.latitude !== 0 && delivery.longitude !== 0) {
      map.flyTo([delivery.latitude, delivery.longitude], 16, { duration: 1.2 });
    }
  };

  return (
    <div
      className={`flex flex-col bg-white overflow-hidden relative ${
        isModal
          ? isFullscreen
            ? 'fixed inset-0 z-[100]'
            : 'relative w-full h-[88vh] max-h-[880px] rounded-2xl shadow-2xl border border-slate-200'
          : 'relative w-full h-[680px] rounded-2xl shadow-lg border border-slate-200'
      }`}
    >
      {/* Top Right Corner Close Button */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-3.5 sm:right-4 z-50 p-2 rounded-xl bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-all shadow-sm flex items-center justify-center cursor-pointer"
          title="Close map"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Unified Top Header Bar with Filters & Controls */}
      <div className="relative z-30 flex flex-wrap items-center justify-between px-3 sm:px-4 py-2.5 bg-white border-b border-slate-200 text-slate-900 shrink-0 gap-2">
        {/* Left: Brand + Filter Dropdowns */}
        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap min-w-0">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
              <Truck className="w-4 h-4" />
            </div>
            <h2 className="hidden md:block text-sm font-bold text-slate-900 tracking-tight whitespace-nowrap">
              Live Map
            </h2>
          </div>

          <div className="hidden md:block h-4 w-px bg-slate-200 shrink-0" />

          {/* Filter Bar */}
          <div ref={filterDropdownRef} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {/* 1. DELIVERY STATUS DROPDOWN */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setStatusDropdownOpen((prev) => !prev);
                  setDriverDropdownOpen(false);
                  setDateDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold border shadow-xs transition-all ${
                  deliveryStatusFilter !== 'All'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-indigo-100'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400'
                }`}
              >
                {deliveryStatusFilter === 'Out for Delivery' ? (
                  <span className="w-2 h-2 rounded-full bg-indigo-300 animate-pulse" />
                ) : deliveryStatusFilter === 'Pending' ? (
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                ) : deliveryStatusFilter === 'Delivered' ? (
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                ) : deliveryStatusFilter === 'Active' ? (
                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                ) : (
                  <Package className="w-3.5 h-3.5 text-slate-400" />
                )}
                <span>
                  {deliveryStatusFilter === 'All'
                    ? `All Deliveries (${counts.all})`
                    : deliveryStatusFilter === 'Active'
                    ? `Active (${counts.active})`
                    : deliveryStatusFilter === 'Out for Delivery'
                    ? `In Transit (${counts.out})`
                    : deliveryStatusFilter === 'Pending'
                    ? `Pending (${counts.pending})`
                    : `Delivered (${counts.delivered})`}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    statusDropdownOpen ? 'rotate-180' : 'text-slate-400'
                  }`}
                />
              </button>

              {/* Status Dropdown Menu */}
              {statusDropdownOpen && (
                <div className="absolute left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-[450] text-xs animate-fadeIn">
                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Filter by Status
                  </div>
                  {[
                    { key: 'All', label: 'All Deliveries', count: counts.all, color: 'bg-slate-400' },
                    { key: 'Active', label: 'Active Deliveries', count: counts.active, color: 'bg-blue-500' },
                    { key: 'Out for Delivery', label: 'In Transit', count: counts.out, color: 'bg-indigo-500' },
                    { key: 'Pending', label: 'Pending Dispatch', count: counts.pending, color: 'bg-amber-500' },
                    { key: 'Delivered', label: 'Delivered Today', count: counts.delivered, color: 'bg-emerald-500' },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setDeliveryStatusFilter(item.key as any);
                        setStatusDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                        deliveryStatusFilter === item.key ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${item.color}`} />
                        <span>{item.label}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-600 text-[10px] font-mono">
                          {item.count}
                        </span>
                        {deliveryStatusFilter === item.key && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                      </div>
                    </button>
                  ))}

                  {/* Special Views Divider */}
                  {(counts.unassigned > 0 || counts.missingCoords > 0) && (
                    <>
                      <div className="border-t border-slate-100 my-1" />
                      <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Special Views
                      </div>
                      {counts.unassigned > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDriverFilter((prev) => (prev === '__unassigned__' ? '' : '__unassigned__'));
                            setStatusDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                            selectedDriverFilter === '__unassigned__' ? 'bg-amber-50 text-amber-800 font-bold' : 'text-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-amber-500" />
                            <span>Unassigned Orders</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                              {counts.unassigned}
                            </span>
                            {selectedDriverFilter === '__unassigned__' && <Check className="w-3.5 h-3.5 text-amber-600" />}
                          </div>
                        </button>
                      )}
                      {counts.missingCoords > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setLocationFilter((prev) => (prev === 'missing' ? 'all' : 'missing'));
                            setActiveSideTab('deliveries');
                            setStatusDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                            locationFilter === 'missing' ? 'bg-red-50 text-red-700 font-bold' : 'text-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                            <span>Missing GPS Postcode</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.2 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                              {counts.missingCoords}
                            </span>
                            {locationFilter === 'missing' && <Check className="w-3.5 h-3.5 text-red-600" />}
                          </div>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 2. RIDER FILTER DROPDOWN */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setDriverDropdownOpen((prev) => !prev);
                  setStatusDropdownOpen(false);
                  setDateDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold border shadow-xs transition-all ${
                  selectedDriverFilter
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-100'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400'
                }`}
              >
                <User className="w-3.5 h-3.5" />
                <span className="max-w-[110px] sm:max-w-[120px] truncate">
                  {selectedDriverFilter === '__unassigned__'
                    ? 'Unassigned'
                    : selectedDriverFilter || `All Riders (${drivers.length})`}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    driverDropdownOpen ? 'rotate-180' : 'text-slate-400'
                  }`}
                />
              </button>

              {/* Driver Dropdown Menu */}
              {driverDropdownOpen && (
                <div className="absolute left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-[450] text-xs max-h-72 overflow-y-auto animate-fadeIn">
                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Filter by Rider
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDriverFilter('');
                      setDriverDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                      !selectedDriverFilter ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-700'
                    }`}
                  >
                    <span>All Drivers / Riders</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-500">{drivers.length}</span>
                      {!selectedDriverFilter && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                    </div>
                  </button>

                  {counts.unassigned > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDriverFilter('__unassigned__');
                        setDriverDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                        selectedDriverFilter === '__unassigned__' ? 'bg-amber-50 text-amber-800 font-bold' : 'text-slate-700'
                      }`}
                    >
                      <span className="text-amber-800 font-medium">👤 Unassigned Orders</span>
                      <div className="flex items-center gap-1.5">
                        <span className="px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                          {counts.unassigned}
                        </span>
                        {selectedDriverFilter === '__unassigned__' && <Check className="w-3.5 h-3.5 text-amber-600" />}
                      </div>
                    </button>
                  )}

                  <div className="border-t border-slate-100 my-1" />
                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Active Riders
                  </div>
                  {drivers.length === 0 ? (
                    <div className="px-3 py-2 text-slate-400 text-xs">No riders registered</div>
                  ) : (
                    drivers.map((dr) => (
                      <button
                        key={dr.id}
                        type="button"
                        onClick={() => {
                          setSelectedDriverFilter(dr.full_name);
                          setDriverDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                          selectedDriverFilter === dr.full_name ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`w-2 h-2 rounded-full shrink-0 ${dr.is_online ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}
                          />
                          <span className="truncate">{dr.full_name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          <span className="px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-600 text-[10px]">
                            {dr.active_orders_count || 0} orders
                          </span>
                          {selectedDriverFilter === dr.full_name && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* 3. DATE FILTER DROPDOWN */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setDateDropdownOpen((prev) => !prev);
                  setStatusDropdownOpen(false);
                  setDriverDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold border shadow-xs transition-all ${
                  datePreset !== 'today'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-blue-100'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>
                  {datePreset === 'today'
                    ? 'Today'
                    : datePreset === 'yesterday'
                    ? 'Yesterday'
                    : datePreset === 'week'
                    ? 'Last 7 Days'
                    : datePreset === 'custom'
                    ? `${fromDate || 'Start'} → ${toDate || 'End'}`
                    : 'All Active'}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    dateDropdownOpen ? 'rotate-180' : 'text-slate-400'
                  }`}
                />
              </button>

              {/* Date Dropdown Menu */}
              {dateDropdownOpen && (
                <div className="absolute left-0 mt-1.5 w-60 bg-white border border-slate-200 rounded-xl shadow-xl py-2 z-[450] text-xs animate-fadeIn">
                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Filter by Date
                  </div>
                  {[
                    { key: 'today', label: '📅 Today' },
                    { key: 'yesterday', label: '📅 Yesterday' },
                    { key: 'week', label: '📅 Last 7 Days' },
                    { key: 'all', label: '📅 All Active Deliveries' },
                    { key: 'custom', label: '📅 Custom Date Range' },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        handleDatePresetChange(item.key as any);
                        if (item.key !== 'custom') setDateDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
                        datePreset === item.key ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-700'
                      }`}
                    >
                      <span>{item.label}</span>
                      {datePreset === item.key && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                    </button>
                  ))}

                  {datePreset === 'custom' && (
                    <div className="p-3 border-t border-slate-200 mt-1 space-y-2 bg-slate-50 rounded-b-xl">
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold block mb-1">From Date</label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-800 focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold block mb-1">To Date</label>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-800 focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDateDropdownOpen(false)}
                        className="w-full mt-1 py-1 px-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-xs"
                      >
                        Apply Dates
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 4. COMPACT SEARCH INPUT */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 pr-6 py-1.5 text-xs bg-white border border-slate-300 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 shadow-xs w-28 sm:w-36 md:w-44 focus:w-52 transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* 5. RESET FILTERS PILL */}
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={resetAllFilters}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 text-xs font-semibold shadow-xs transition-all"
                title="Reset all filters"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Reset ({activeFiltersCount})</span>
              </button>
            )}
          </div>
        </div>

        {/* Right: Map Controls, Layer Toggles, Panel Toggle */}
        <div className={`flex items-center gap-1.5 sm:gap-2 flex-wrap ${onClose ? 'pr-11 sm:pr-12' : ''}`}>
          {/* Layer Toggles */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 border border-slate-200 text-xs">
            <button
              type="button"
              onClick={() => setShowRidersLayer((prev) => !prev)}
              className={`flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-lg font-semibold transition-all ${
                showRidersLayer
                  ? 'bg-white text-emerald-700 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Toggle Riders on map"
            >
              <span>🚴 Riders ({counts.totalRiders})</span>
            </button>
            <button
              type="button"
              onClick={() => setShowDeliveriesLayer((prev) => !prev)}
              className={`flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-lg font-semibold transition-all ${
                showDeliveriesLayer
                  ? 'bg-white text-indigo-700 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Toggle Deliveries on map"
            >
              <span>📦 Orders ({counts.all})</span>
            </button>
          </div>

          {/* Auto Refresh Toggle */}
          <button
            type="button"
            onClick={() => setAutoRefresh((prev) => !prev)}
            className={`hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              autoRefresh
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
            }`}
            title="Auto-refresh every 20 seconds"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            Auto-sync {autoRefresh ? 'ON' : 'OFF'}
          </button>

          {/* Manual Refresh */}
          <button
            type="button"
            onClick={() => void refetch()}
            className="p-1.5 sm:p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
            title="Refresh riders and deliveries"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-indigo-600' : ''}`} />
          </button>

          {/* Fit Bounds */}
          <button
            type="button"
            onClick={fitAllMarkers}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold transition-colors"
            title="Fit all markers on screen"
          >
            <Navigation className="w-3.5 h-3.5 text-indigo-600" />
            Fit All
          </button>

          {/* Sidebar Toggle */}
          <button
            type="button"
            onClick={() => setSidebarOpen((prev) => !prev)}
            className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold transition-colors"
          >
            {sidebarOpen ? 'Hide Panel' : 'Show Panel'}
          </button>

          {/* Fullscreen Toggle */}
          {isModal && (
            <button
              type="button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              className="p-1.5 sm:p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Main Map Viewport & Side Panel */}
      <div className="relative flex-1 flex overflow-hidden">
        {/* Map Container */}
        <div className="relative flex-1 w-full h-full bg-slate-100">
          <div ref={mapContainerRef} className="w-full h-full z-0" />

          {/* Visual Legend Bar (Bottom Left) */}
          <div className="absolute bottom-4 left-3 z-[400] hidden sm:flex items-center gap-3 bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl px-3 py-2 text-[11px] text-slate-700 shadow-lg">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              Rider Online
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              Rider Idle
            </span>
            <span className="border-l border-slate-200 h-3" />
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              In Transit
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              Pending
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              Delivered
            </span>
          </div>

          {/* Alert Overlay if no coordinates plotted */}
          {!isLoading && driversWithLocation.length === 0 && deliveriesWithLocation.length === 0 && (
            <div className="absolute top-6 left-4 right-4 sm:right-auto sm:max-w-md z-[500] bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl p-4 shadow-xl text-slate-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-slate-800">Waiting for GPS Coordinates</h4>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    No active riders have reported GPS coordinates yet, and orders are awaiting postcode geocoding.
                    When riders open their delivery dashboard, their live location and delivery drop-off pins will appear here automatically.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Side Panel: Riders & Deliveries Tabs */}
        {sidebarOpen && (
          <div className="w-80 sm:w-92 bg-white border-l border-slate-200 flex flex-col z-10 shrink-0 text-slate-800 shadow-sm">
            {/* Panel Tabs Header */}
            <div className="px-3 pt-3 pb-2 border-b border-slate-200 flex items-center justify-between gap-2 bg-slate-50/50">
              <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200 text-xs w-full">
                <button
                  type="button"
                  onClick={() => setActiveSideTab('deliveries')}
                  className={`flex-1 py-1.5 px-2 rounded-lg font-bold text-center transition-all ${
                    activeSideTab === 'deliveries'
                      ? 'bg-white text-indigo-700 shadow-xs border border-slate-200/80'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Deliveries ({deliveries.length}{deliveries.length !== counts.all ? `/${counts.all}` : ''})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSideTab('riders')}
                  className={`flex-1 py-1.5 px-2 rounded-lg font-bold text-center transition-all ${
                    activeSideTab === 'riders'
                      ? 'bg-white text-indigo-700 shadow-xs border border-slate-200/80'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Riders ({filteredDrivers.length}{filteredDrivers.length !== drivers.length ? `/${drivers.length}` : ''})
                </button>
              </div>
            </div>

            {/* Search Input */}
            <div className="p-3 border-b border-slate-200 bg-white">
              <div className="relative w-full">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={
                    activeSideTab === 'deliveries'
                      ? 'Search order #, customer, address...'
                      : 'Search rider name...'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-colors"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* TAB CONTENT: DELIVERIES LIST */}
            {activeSideTab === 'deliveries' && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50/40">
                {counts.missingCoords > 0 && locationFilter !== 'plotted' && (
                  <div className="flex items-center justify-between p-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span className="truncate">{counts.missingCoords} without GPS pin</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLocationFilter(locationFilter === 'missing' ? 'all' : 'missing')}
                      className="text-[11px] font-bold text-amber-700 hover:text-amber-900 underline shrink-0 ml-2"
                    >
                      {locationFilter === 'missing' ? 'Show All' : 'Filter'}
                    </button>
                  </div>
                )}
                {deliveries.length === 0 ? (
                  <div className="text-center py-10 text-xs text-slate-400">
                    No deliveries match the selected filter.
                  </div>
                ) : (
                  deliveries.map((delivery) => {
                    const isSelected = selectedEntityId === delivery.id;
                    const hasCoords = delivery.latitude !== 0 && delivery.longitude !== 0;
                    const status = delivery.delivery_status || 'Pending';

                    let badgeColor = 'bg-amber-50 text-amber-700 border-amber-200';
                    if (status === 'Out for Delivery') {
                      badgeColor = 'bg-indigo-50 text-indigo-700 border-indigo-200';
                    } else if (status === 'Delivered') {
                      badgeColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                    }

                    return (
                      <div
                        key={delivery.id}
                        onClick={() => focusDelivery(delivery)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-50/70 border-indigo-400 ring-1 ring-indigo-400 shadow-sm'
                            : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-xs'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold text-xs text-slate-800">
                                {delivery.invoice_name}
                              </span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badgeColor}`}>
                                {status}
                              </span>
                            </div>
                            <p className="text-xs font-semibold text-slate-900 mt-1">
                              {delivery.customer_name || delivery.delivery_recipient_name || 'Customer'}
                            </p>
                          </div>

                          {hasCoords && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                focusDelivery(delivery);
                              }}
                              className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors shrink-0 border border-indigo-100"
                              title="Center on map"
                            >
                              <MapPin className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Address & Driver Details */}
                        <div className="mt-2 text-[11px] text-slate-500 space-y-1">
                          {delivery.delivery_address && (
                            <p className="line-clamp-2 leading-relaxed">
                              📍 {delivery.delivery_address}{' '}
                              {delivery.delivery_postal_code ? `(${delivery.delivery_postal_code})` : ''}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                            <span className="text-slate-600">
                              {delivery.delivery_driver ? (
                                <span className="text-indigo-600 font-medium">🚴 {delivery.delivery_driver}</span>
                              ) : (
                                <span className="text-slate-400 italic">Unassigned</span>
                              )}
                            </span>
                            {hasCoords ? (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                                Mapped
                              </span>
                            ) : (
                              <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                No GPS Postcode
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* TAB CONTENT: RIDERS LIST */}
            {activeSideTab === 'riders' && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50/40">
                {drivers.length === 0 ? (
                  <div className="text-center py-10 text-xs text-slate-400">
                    No delivery persons found.
                  </div>
                ) : (
                  drivers.map((driver) => {
                    const isSelected = selectedEntityId === driver.id;
                    const isOnline = driver.is_online;
                    const hasLoc = driver.has_location;

                    return (
                      <div
                        key={driver.id}
                        onClick={() => focusDriver(driver)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-50/70 border-indigo-400 ring-1 ring-indigo-400 shadow-sm'
                            : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-xs'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                                isOnline
                                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                  : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}
                            >
                              <User className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900 leading-tight">
                                {driver.full_name}
                              </p>
                              <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    isOnline
                                      ? 'bg-emerald-500 animate-pulse'
                                      : hasLoc
                                      ? 'bg-amber-500'
                                      : 'bg-slate-400'
                                  }`}
                                />
                                {isOnline
                                  ? 'Online & tracking'
                                  : hasLoc
                                  ? `Last seen ${driver.minutes_ago ?? ''}m ago`
                                  : 'No GPS reported'}
                              </p>
                            </div>
                          </div>

                          {hasLoc && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                focusDriver(driver);
                              }}
                              className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors border border-indigo-100"
                              title="Center on map & trace deliveries"
                            >
                              <MapPin className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Deliveries carried */}
                        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1 text-[11px]">
                            <Package className="w-3.5 h-3.5 text-indigo-600" />
                            Carrying:{' '}
                            <strong className="text-slate-800">
                              {driver.active_orders_count} deliveries
                            </strong>
                          </span>
                          {hasLoc ? (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                              GPS Active
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400">Offline</span>
                          )}
                        </div>

                        {/* Mini preview of carried orders */}
                        {driver.orders && driver.orders.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {driver.orders.map((ord) => (
                              <div
                                key={ord.invoice_name}
                                className="p-1.5 bg-slate-50 rounded-lg border border-slate-200 text-[11px] flex justify-between items-center"
                              >
                                <span className="font-medium text-slate-700">{ord.invoice_name}</span>
                                <span className="text-indigo-600 font-medium">{ord.customer_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Footer Summary */}
            <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 flex items-center justify-between">
              <span>Plotted on Map:</span>
              <strong className="text-indigo-600 font-bold">
                {deliveriesWithLocation.length} Orders • {driversWithLocation.length} Riders
              </strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
